// apps/client/src/world/spaces/shop.ts (M1-T14) — the shop interior (§8.4): the toy-room shell, the counter with Bramble behind it, the
// loan desk and today's stock on display plinths. Layout tiles come from tuning.spaces.shop (x → X, y → Z).
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { getItem, tuning, type CatalogId, type SurfaceEntry } from '@whiskerton/content';
import type { Rect, Vec2 } from '@whiskerton/shared';
import type { AssetService } from '../../assets/service';
import { disposeDoll, toyMaterial } from '../../characters/doll';
import { createShopkeeper } from '../../characters/npcs';
import type { SpaceSceneDef } from '../../game/spaces';
import { useGame } from '../../state/store';
import { buildRoomShell, type BuiltRoom, type RoomBuildOptions } from '../interior';

export interface ShopLayout { counter: THREE.Vector3; counterSize: [number, number]; shopkeeper: THREE.Vector3; loanDesk: THREE.Vector3; door: THREE.Vector3 }
/** Tile centres from tuning.spaces.shop (x → X, y → Z). */
export function shopLayout(): ShopLayout {
  const s = tuning.spaces.shop, [cx, cy, cw, ch] = s.counter;
  return {
    counter: new THREE.Vector3(cx + cw / 2, 0, cy + ch / 2), counterSize: [cw, ch],
    shopkeeper: new THREE.Vector3(s.shopkeeper[0], 0, s.shopkeeper[1]),
    loanDesk: new THREE.Vector3(s.loanDesk[0] + 0.5, 0, s.loanDesk[1] + 0.5),
    door: new THREE.Vector3(s.door[0] + 0.5, 0, s.door[1] + 0.5),
  };
}
const SHOP_WALL = 'wall.honey_diamonds', SHOP_FLOOR = 'floor.oak_planks';
// Presentation (DESIGN.md toy room): counter wood, loan desk leaf-700, plinths cream-300.
const COUNTER = '#C9A27E', COUNTER_TOP = '#B07A4F', DESK = '#3A7D44', LEDGER = '#FFF6E0', PLINTH = '#EAD6AE';
const DESK_SIZE: [number, number] = [0.9, 0.7], PLINTH_SIZE: [number, number] = [1, 0.8], ON_PLINTH = 0.85;
/** Counter height and the step Bramble stands on behind it, in metres: the steep 3/4 camera then sees him from the apron up
 *  (residents are 1.5 m tall). */
const COUNTER_H = 0.8, STEP_H = 0.18;
/** Where a walking resident's centre must stay away from furniture and walls (about the resident's radius). */
const CLEAR = 0.3;

/** Display spots for today's stock (x, z): the back corners beside the counter, down the left wall and the two front corners, all
 *  clear of Bramble, the loan desk and the walks from the door to the counter and the desk. */
function stockSpots(n: number): THREE.Vector3[] {
  const s = tuning.spaces.shop, left = 0.9, right = s.w - 1;
  const spots: [number, number][] = [[left, 0.9], [left, 2.3], [left, 3.7], [right, 0.9], [left + 0.7, s.h - 1], [right, s.h - 1.4]];
  for (let k = 0, base = spots.length; base + k < n; k++) spots.push([2.4 + k * 1.2, s.h - 1]);   // more than tuning's six: along the front
  return spots.slice(0, n).map(([x, z]) => new THREE.Vector3(x, 0, z));
}
const rectAt = (c: THREE.Vector3, [w, d]: [number, number]): Rect => ({ x: c.x - w / 2, y: c.z - d / 2, w, h: d });
const near = (p: Vec2, r: Rect, pad: number) => p.x > r.x - pad && p.x < r.x + r.w + pad && p.y > r.y - pad && p.y < r.y + r.h + pad;

export async function buildShopScene(o: { assets: Pick<AssetService, 'model' | 'rig'>; daily: readonly CatalogId[]; paint?: RoomBuildOptions['paint']; shopkeeper?: () => Promise<THREE.Object3D> }):
  Promise<{ scene: THREE.Scene; room: BuiltRoom; layout: ShopLayout; update(dt: number): void; blocked: Rect[]; dispose(): void }> {
  const s = tuning.spaces.shop, layout = shopLayout();
  const scene = new THREE.Scene(); scene.background = new THREE.Color('#F3E4C6');
  const room: BuiltRoom = buildRoomShell({ w: s.w, h: s.h, wallpaper: (getItem(SHOP_WALL) as SurfaceEntry).pattern, floor: (getItem(SHOP_FLOOR) as SurfaceEntry).pattern }, { paint: o.paint });
  scene.add(room.group);
  const own: THREE.BufferGeometry[] = [];
  const box = (w: number, h: number, d: number, r: number) => { const g = new RoundedBoxGeometry(w, h, d, 3, r); own.push(g); return g; };
  const solid = (m: THREE.Mesh) => { m.castShadow = m.receiveShadow = true; return m; };

  const [cw, cd] = layout.counterSize;
  const counter = solid(new THREE.Mesh(box(cw, COUNTER_H, cd * 0.8, 0.08), toyMaterial(COUNTER)));
  counter.name = 'counter'; counter.position.set(layout.counter.x, COUNTER_H / 2, layout.counter.z);
  const top = solid(new THREE.Mesh(box(cw + 0.15, 0.1, cd * 0.8 + 0.15, 0.04), toyMaterial(COUNTER_TOP)));
  top.position.set(0, COUNTER_H / 2 + 0.02, 0); counter.add(top);
  const desk = solid(new THREE.Mesh(box(DESK_SIZE[0], 0.9, DESK_SIZE[1], 0.08), toyMaterial(DESK)));
  desk.name = 'loanDesk'; desk.position.set(layout.loanDesk.x, 0.45, layout.loanDesk.z);
  const ledger = solid(new THREE.Mesh(box(0.42, 0.06, 0.3, 0.02), toyMaterial(LEDGER)));
  ledger.position.set(0, 0.48, 0); ledger.rotation.y = -0.25; desk.add(ledger);

  let keeperUpdate = (_dt: number) => {};
  let keeperDispose = () => disposeDoll(keeper);
  const keeper = o.shopkeeper ? await o.shopkeeper() : await (async () => {
    const npc = createShopkeeper(await o.assets.rig());
    keeperUpdate = npc.update; keeperDispose = npc.dispose;
    return npc.root;
  })();
  keeper.name = 'shopkeeper'; keeper.position.copy(layout.shopkeeper).setY(STEP_H);
  const step = solid(new THREE.Mesh(box(1.1, STEP_H, 0.6, 0.05), toyMaterial(COUNTER_TOP)));
  step.name = 'step'; step.position.copy(layout.shopkeeper).setY(STEP_H / 2);
  scene.add(counter, desk, step, keeper);

  // Today's stock on low display plinths, each model shrunk to sit on its plinth.
  const spots = stockSpots(o.daily.length);
  await Promise.all(o.daily.map(async (id, i) => {
    const plinth = solid(new THREE.Mesh(box(PLINTH_SIZE[0], 0.3, PLINTH_SIZE[1], 0.06), toyMaterial(PLINTH)));
    plinth.name = 'plinth'; plinth.position.copy(spots[i]!).setY(0.15);
    const model = await o.assets.model(id);
    const size = new THREE.Box3().setFromObject(model).getSize(new THREE.Vector3());
    model.scale.multiplyScalar(Math.min(0.6, (PLINTH_SIZE[1] * ON_PLINTH) / Math.max(size.x, size.z, 1e-6)));
    model.position.copy(spots[i]!).setY(0.3);
    model.name = `stock:${id}`;
    scene.add(plinth, model);
  }));
  // Where residents cannot stand: the counter and everything behind it (Bramble's side), the loan desk and the plinths.
  const [x, y, w, h] = s.counter;
  const blocked: Rect[] = [{ x, y: 0, w, h: y + h }, rectAt(layout.loanDesk, DESK_SIZE), ...spots.map((p) => rectAt(p, PLINTH_SIZE))];
  return {
    scene, room, layout, blocked, update: (dt: number) => keeperUpdate(dt),
    dispose() {
      room.dispose();
      for (const g of own) g.dispose();
      keeperDispose();                        // Bramble's baked mesh, bone texture and idle mixer (the rig's kit meshes are shared and stay)
    },
  };
}

export const space: SpaceSceneDef = {
  kind: 'shop',
  async build(_snap, game) {
    const s = tuning.spaces.shop;
    const shop = await buildShopScene({ assets: game.assets, daily: useGame.getState().you?.shop?.daily.map((d) => d.catalogId) ?? [] });
    const walkable = (p: Vec2) => p.x > 0.35 && p.y > 0.35 && p.x < s.w - 0.35 && p.y < s.h - 0.1 && !shop.blocked.some((r) => near(p, r, CLEAR));
    return {
      scene: shop.scene, bounds: { x: 0, y: 0, w: s.w, h: s.h },
      heightAt: () => 0,
      walkable, canMove: (_from, to) => walkable(to),
      tick: (dt, minute) => { shop.update(dt); shop.room.light(minute); },   // Bramble idles; the shop follows the clock (light and look)
      dispose: () => shop.dispose(),
    };
  },
};
